"use client";

/**
 * Post-signup personalization wizard (PANDA-1523) — a short, one-time,
 * 3-step flow: theme, role, company size. Rendered INLINE by callers, same
 * pattern as OwnershipQuestionsInline (no modal chrome baked in here) so it
 * drops into either the project-page in-canvas slot or a home-page overlay
 * without dragging a dialog dependency along.
 *
 * Distinct from the ownership questions (lib/ownership/ownership-questions):
 * this captures who's building, not what's being built, and runs once per
 * account rather than once per project.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Loader2, Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  COMPANY_SIZE_OPTIONS,
  ONBOARDING_STEP_KEYS,
  ROLE_OPTIONS,
  type PersonalizationAnswers,
  type ThemeChoice,
} from "@/lib/onboarding/personalization";
import {
  trackOnboardingAnswerSelected,
  trackOnboardingCompleted,
  trackOnboardingSkipped,
  trackOnboardingStepCompleted,
  trackOnboardingStepViewed,
  type OnboardingSurface,
} from "@/lib/analytics";

interface PersonalizationWizardInlineProps {
  onComplete: (answers: PersonalizationAnswers) => void;
  /**
   * "Skip for now" — the way out. Note it is NOT what makes the wizard
   * one-time: that is the server-side stamp the caller writes the moment it
   * renders this (markPersonalizationShown), precisely because no handler here
   * can be relied on to run — the user may just refresh or close the tab.
   */
  onSkip: () => void;
  submitting?: boolean;
  /** Which surface rendered the wizard — segments the PostHog funnel. */
  surface: OnboardingSurface;
  /** Attached to the onboarding events when the caller knows it. */
  email?: string | null;
}

/** MUST stay index-aligned with ONBOARDING_STEP_KEYS (the analytics labels). */
const STEP_TITLES = [
  "Pick your style",
  "Which role fits you best?",
  "How many people work at your company?",
];

/** Light/dark mock preview card — the "visual card" the ticket asks for
 *  instead of a plain text label. */
function ThemePreviewCard({
  theme,
  active,
  onClick,
  disabled,
}: {
  theme: ThemeChoice;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  const isDark = theme === "dark";
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex flex-1 flex-col items-center gap-2.5 rounded-2xl border px-3 py-4 transition-all duration-200 active:scale-[0.99]",
        active
          ? "border-[#D52A26]/60 bg-[#D52A26]/[0.06] dark:border-[#e3251b]/60 dark:bg-[#e3251b]/[0.08]"
          : "border-transparent bg-zinc-50 hover:bg-zinc-100 dark:bg-white/[0.03] dark:hover:bg-white/[0.06]",
        disabled && "cursor-not-allowed opacity-70",
      )}
    >
      <span
        className={cn(
          "flex h-16 w-full flex-col gap-1 overflow-hidden rounded-lg p-2 ring-1 ring-inset",
          isDark
            ? "bg-[#0b0f19] ring-white/10"
            : "bg-white ring-black/10",
        )}
        aria-hidden
      >
        <span className={cn("h-1.5 w-8 rounded-full", isDark ? "bg-white/25" : "bg-zinc-300")} />
        <span className={cn("mt-1 h-1.5 w-full rounded-full", isDark ? "bg-white/10" : "bg-zinc-100")} />
        <span className={cn("h-1.5 w-3/4 rounded-full", isDark ? "bg-white/10" : "bg-zinc-100")} />
      </span>
      <span className="flex items-center gap-1.5">
        {isDark ? (
          <Moon className="h-3.5 w-3.5 text-zinc-500 dark:text-zinc-400" />
        ) : (
          <Sun className="h-3.5 w-3.5 text-zinc-500 dark:text-zinc-400" />
        )}
        <span
          className={cn(
            "text-[13px] font-medium",
            active ? "text-zinc-900 dark:text-white" : "text-zinc-700 dark:text-zinc-200",
          )}
        >
          {isDark ? "Dark Mode" : "Light Mode"}
        </span>
      </span>
      {active ? (
        <Check className="h-3.5 w-3.5 text-[#D52A26] dark:text-[#e3251b]" strokeWidth={2.5} />
      ) : null}
    </button>
  );
}

function OptionGrid({
  options,
  value,
  onSelect,
  disabled,
  label,
}: {
  options: { value: string; label: string }[];
  value: string | null;
  onSelect: (value: string) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <div className="mt-4 grid grid-cols-2 gap-2" role="radiogroup" aria-label={label}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onSelect(opt.value)}
            className={cn(
              "flex items-center justify-between gap-2 rounded-2xl border px-4 py-3 text-left transition-all duration-200 active:scale-[0.99]",
              active
                ? "border-[#D52A26]/60 bg-[#D52A26]/[0.06] dark:border-[#e3251b]/60 dark:bg-[#e3251b]/[0.08]"
                : "border-transparent bg-zinc-50 hover:bg-zinc-100 dark:bg-white/[0.03] dark:hover:bg-white/[0.06]",
              disabled && "cursor-not-allowed opacity-70",
            )}
          >
            <span
              className={cn(
                "text-[14px] font-medium",
                active ? "text-zinc-900 dark:text-white" : "text-zinc-700 dark:text-zinc-200",
              )}
            >
              {opt.label}
            </span>
            {active ? (
              <Check className="h-4 w-4 shrink-0 text-[#D52A26] dark:text-[#e3251b]" strokeWidth={2.5} />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export function PersonalizationWizardInline({
  onComplete,
  onSkip,
  submitting = false,
  surface,
  email,
}: PersonalizationWizardInlineProps) {
  const [step, setStep] = useState(0);
  const [theme, setTheme] = useState<ThemeChoice | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [companySize, setCompanySize] = useState<string | null>(null);

  const isLast = step === STEP_TITLES.length - 1;
  const canAdvance = useMemo(() => {
    if (step === 0) return theme !== null;
    if (step === 1) return role !== null;
    return companySize !== null;
  }, [step, theme, role, companySize]);

  // ── PostHog onboarding funnel ────────────────────────────────────────────
  // Answers are tracked from here rather than from the submit handlers so a
  // per-step drop-off is visible: the callers only ever see the terminal
  // complete/skip, never the steps in between.
  const stepKey = ONBOARDING_STEP_KEYS[step];
  const stepContext = useMemo(
    () => ({ surface, email, stepIndex: step + 1, questionKey: stepKey }),
    [surface, email, step, stepKey],
  );
  const currentValue = step === 0 ? theme : step === 1 ? role : companySize;

  // Once per step per mount — stepping back and forth is navigation, not a
  // fresh impression, and would double-count the funnel's entry step.
  const viewedStepsRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (viewedStepsRef.current.has(step)) return;
    viewedStepsRef.current.add(step);
    trackOnboardingStepViewed(stepContext);
  }, [step, stepContext]);

  /** Re-picking the same value is a no-op click, not a new answer. */
  const selectAnswer = useCallback(
    (value: string, previous: string | null) => {
      if (value === previous) return;
      trackOnboardingAnswerSelected({ ...stepContext, value });
    },
    [stepContext],
  );

  const handleNext = () => {
    if (!canAdvance || submitting) return;
    trackOnboardingStepCompleted({ ...stepContext, value: currentValue });
    if (isLast) {
      trackOnboardingCompleted({
        surface,
        email,
        answers: { theme, role, companySize },
      });
      onComplete({ theme, role, companySize });
    } else {
      setStep((s) => Math.min(s + 1, STEP_TITLES.length - 1));
    }
  };

  const handleSkip = () => {
    trackOnboardingSkipped({
      ...stepContext,
      answers: { theme, role, companySize },
    });
    onSkip();
  };

  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div className="flex h-full flex-col px-5 py-6 sm:px-6">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col overflow-hidden">
        <div className="flex shrink-0 items-center justify-between">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-medium text-zinc-600 dark:bg-white/[0.06] dark:text-zinc-300">
            Quick setup
          </span>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5" aria-hidden>
              {STEP_TITLES.map((_, i) => (
                <span
                  key={i}
                  className={cn(
                    "h-1.5 rounded-full transition-all",
                    i === step
                      ? "w-5 bg-zinc-900 dark:bg-white"
                      : i < step
                        ? "w-1.5 bg-zinc-400 dark:bg-zinc-500"
                        : "w-1.5 bg-zinc-300 dark:bg-zinc-700",
                  )}
                />
              ))}
            </div>
            <button
              type="button"
              disabled={submitting}
              onClick={handleSkip}
              className="text-xs font-medium text-zinc-400 transition-colors hover:text-zinc-700 disabled:cursor-not-allowed dark:text-zinc-500 dark:hover:text-zinc-200"
            >
              Skip for now
            </button>
          </div>
        </div>

        <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
          <p className="mt-5 text-sm text-zinc-500 dark:text-zinc-400">
            Helps us tailor Panda to how you work.
          </p>
          <h2 className="mt-1.5 text-xl font-bold tracking-tight text-zinc-900 dark:text-white">
            {STEP_TITLES[step]}
          </h2>

          {step === 0 ? (
            <div className="mt-4 flex gap-2" role="radiogroup" aria-label={STEP_TITLES[0]}>
              <ThemePreviewCard
                theme="light"
                active={theme === "light"}
                disabled={submitting}
                onClick={() => {
                  selectAnswer("light", theme);
                  setTheme("light");
                }}
              />
              <ThemePreviewCard
                theme="dark"
                active={theme === "dark"}
                disabled={submitting}
                onClick={() => {
                  selectAnswer("dark", theme);
                  setTheme("dark");
                }}
              />
            </div>
          ) : step === 1 ? (
            <OptionGrid
              options={ROLE_OPTIONS}
              value={role}
              onSelect={(value) => {
                selectAnswer(value, role);
                setRole(value);
              }}
              disabled={submitting}
              label={STEP_TITLES[1]}
            />
          ) : (
            <OptionGrid
              options={COMPANY_SIZE_OPTIONS}
              value={companySize}
              onSelect={(value) => {
                selectAnswer(value, companySize);
                setCompanySize(value);
              }}
              disabled={submitting}
              label={STEP_TITLES[2]}
            />
          )}
        </div>

        <div className="shrink-0 pt-6">
          <div className="flex items-center gap-2">
            {step > 0 ? (
              <button
                type="button"
                disabled={submitting}
                onClick={handleBack}
                aria-label="Previous step"
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-600 transition-colors hover:bg-zinc-200 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white/[0.06] dark:text-zinc-300 dark:hover:bg-white/[0.1]"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            ) : null}

            <button
              type="button"
              disabled={!canAdvance || submitting}
              onClick={handleNext}
              className={cn(
                "inline-flex flex-1 items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-all",
                canAdvance && !submitting
                  ? "bg-panda-gradient text-white shadow-sm hover:brightness-105"
                  : "bg-zinc-100 text-zinc-400 dark:bg-white/[0.06] dark:text-zinc-500",
                "disabled:cursor-not-allowed",
              )}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : isLast ? (
                "Continue"
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
